import { Box, Typography } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useFirestore } from '../hooks/useFirebase';
import {
  currentAuthAtom,
  popupAlertAtom,
  teamIdsJotaiAtom,
  teamsJotaiAtom
} from '../jotai/atoms';
import flex from '../styles/flex.module.scss';
import { analytics } from '../utils/analytics';
import { convertToTeam } from '../utils/converters';
import { LoadingPage } from './LoadingPage';
import { TeamIcon } from './TeamIcon';

const styles = {
  root: {
    flex: 1,
    padding: 4
  },
  header: {
    marginBottom: 3
  }
} as const;

export function TeamSelect() {
  const history = useHistory();
  const firestore = useFirestore();
  const userInfo = useAtomValue(currentAuthAtom);
  const uid = userInfo?.uid;
  const [initialized, setInitialized] = React.useState(false);
  const [teamIds, setTeamIds] = useAtom(teamIdsJotaiAtom);
  const setAlert = useSetAtom(popupAlertAtom);

  // 所属チームの情報を取得する
  React.useEffect(() => {
    if (!uid) return;

    setInitialized(false);

    return firestore
      .collection('teams')
      .where('admins', 'array-contains', uid)
      .withConverter(convertToTeam)
      .onSnapshot({
        next: snapshot => {
          if (!snapshot.metadata.fromCache) {
            const ids = snapshot.docs.map(doc => doc.id);
            setTeamIds(ids);
            setInitialized(true);
          }
        },
        error: e => {
          setInitialized(true);
          setAlert({
            severity: 'error',
            children: e.message
          });
        }
      });
  }, [uid, firestore, setTeamIds, setAlert]);

  React.useEffect(() => {
    if (initialized && teamIds.length === 0) {
      // １つもチームがない場合は /register に遷移する
      analytics.viewRegister();
      history.push('/register');
    }
  }, [teamIds, initialized]);

  return initialized ? (
    <Box sx={styles.root}>
      <Typography variant="h6" sx={styles.header}>
        所属チーム
      </Typography>
      <div className={flex.horizontal}>
        {teamIds.map(id => (
          <TeamSelectItem key={id} id={id} />
        ))}
      </div>
    </Box>
  ) : (
    <LoadingPage />
  );
}

interface TeamSelectItemProps {
  id: string;
}

function TeamSelectItem(props: TeamSelectItemProps) {
  const history = useHistory();
  const team = useAtomValue(teamsJotaiAtom(props.id));

  return team ? (
    <div
      className={flex.vertical}
      style={{ marginRight: 32 }}
      onClick={() => history.push(`/${props.id}/members`)}
    >
      <TeamIcon id={props.id} button />
      <Typography variant="body1">{team.name}</Typography>
    </div>
  ) : null;
}
